import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Dialog from '../../../../common/element/Dialog'
import Tabs from '../../../../common/element/Tabs/Tabs'
import TabList from '../../../../common/element/Tabs/TabList'
import TabNav from '../../../../common/element/Tabs/TabNav'
import TabContent from '../../../../common/element/Tabs/TabContent'
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary'
// eslint-disable-next-line no-unused-vars
import BtnLight from '../../../../common/element/Buttons/btnLight'
import Segment from '../../../../common/element/Segment'
import Select from '../../../../common/element/Select'
import Upload from '../../../../common/element/Upload'
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader"
import CommonPhoneInput from '../../../../common/CommonPhoneInput/CommonPhoneInput'

import { getCountryList, getStateList, getCityList, clearStates, clearCities } from '../../../../redux/countryStateCity/cityStateCountrySlice'
import { selectCountryList, selectStateList, selectCityList } from '../../../../redux/countryStateCity/cityStateCountrySelector'


import SweetAlertService from '../../../../common/SweetAlert/SweetAlert'
import { addCoach, updateCoach, clearAddCoach, clearUpdateCoach, getCoachById } from '../../../../redux/Coach/coachSlice'
import { selectAddCoachData, selectAddCoachLoading, selectAddCoachError, selectUpdateCoachData, selectUpdateCoachLoading, selectUpdateCoachError } from '../../../../redux/Coach/coachSelector'

const AddEditCoach = ({ dialogOpen, onDialogClose, coachData, onSuccess = "" }) => {

  // Get data from Redux
  const dispatch = useDispatch();

  // Get data from Redux
  const countryList = useSelector(selectCountryList);
  const stateList = useSelector(selectStateList);
  const cityList = useSelector(selectCityList);

  // Coach API selectors
  const addCoachData = useSelector(selectAddCoachData);
  // eslint-disable-next-line no-unused-vars
  const addCoachLoading = useSelector(selectAddCoachLoading);
  const addCoachError = useSelector(selectAddCoachError);

  const updateCoachData = useSelector(selectUpdateCoachData);
  // eslint-disable-next-line no-unused-vars
  const updateCoachLoading = useSelector(selectUpdateCoachLoading);
  const updateCoachError = useSelector(selectUpdateCoachError);

  const [tab, setTab] = useState("tab1");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [tabsValidated, setTabsValidated] = useState({
    tab1: false,
    tab2: true, // Changed default to true since docs are optional now
    tab3: true 
  });

  // Track existing documents and removed document IDs
  // eslint-disable-next-line no-unused-vars
  const [existingDocuments, setExistingDocuments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [removedDocumentIds, setRemovedDocumentIds] = useState([]);

  // Fetch country list on component mount
  useEffect(() => {
    dispatch(getCountryList());
  }, [dispatch]);

  const initialFormState = {
    // Basic Details
    coach_type: "",
    title: "",
    first_name: "",
    last_name: "",
    company_name: "",
    display_name: "",
    email: "",
    work_phone: "",
    mobile_number: "",

    // Other Details
    isPortalEnabled: true,
    documents: [],

    // Address Details
    addressLine1: "",
    addressLine2: "",
    city: 0,
    state: 0,
    pincode: "",
    phone_landline: "",
    country: 0,
    remarks: ""
  };

  const initialErrorState = {
    coach_type: "",
    title: "",
    first_name: "",
    last_name: "",
    company_name: "",
    display_name: "",
    email: "",
    work_phone: "",
    mobile_number: "",
    isPortalEnabled: "",
    documents: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    phone_landline: "",
    country: "",
    remarks: ""
  };

  // Form state
  const [formData, setFormData] = useState(initialFormState);
  const [formError, setFormErrors] = useState(initialErrorState);

  const isEdit = !!coachData?.id;

  // Handle API success response
  useEffect(() => {
    if (addCoachData) {
      SweetAlertService.showSuccess({
        title: 'Success!',
        message: 'Coach has been added successfully.',
        onConfirm: () => {
          dispatch(clearAddCoach());
          handleClose();
          if (onSuccess) onSuccess();
        }
      });
    }
  }, [addCoachData, dispatch]);

  useEffect(() => {
    if (updateCoachData) {
      SweetAlertService.showSuccess({
        title: 'Success!',
        message: 'Coach has been updated successfully.',
        onConfirm: () => {
          dispatch(clearUpdateCoach());
          // Refresh coach data after update
          if (coachData?.id) {
            dispatch(getCoachById(coachData.id));
          }
          handleClose();
          if (onSuccess) onSuccess();
        }
      });
    }
  }, [updateCoachData, dispatch, coachData?.id]);

  // Handle API error response
  useEffect(() => {
    if (addCoachError) {
      SweetAlertService.showError({
        title: 'Error!',
        message: addCoachError || 'Failed to add coach. Please try again.',
        onConfirm: () => {
          dispatch(clearAddCoach());
          setButtonDisabled(false);
        }
      });
    }
  }, [addCoachError, dispatch]);

  useEffect(() => {
    if (updateCoachError) {
      SweetAlertService.showError({
        title: 'Error!',
        message: updateCoachError || 'Failed to update coach. Please try again.',
        onConfirm: () => {
          dispatch(clearUpdateCoach());
          setButtonDisabled(false);
        }
      });
    }
  }, [updateCoachError, dispatch]);

  // Reset form when dialog closes or opens
  useEffect(() => {
    if (dialogOpen) {
      if (coachData?.id) {
        // Edit mode - populate with existing data
        const addressLines = coachData.address ? coachData.address.split('\n') : ['', ''];
        // Map coachData.documents (from API) into file-like objects Upload expects
        const mappedDocuments = (coachData.documents || []).map((doc, idx) => ({
          name: doc.document_url?.split('/')?.pop() || `remote_file_${idx}`,
          size: doc.document_size ? parseFloat(doc.document_size.replace(/[^0-9.]/g, '')) * 1000 : 0,
          type: doc.document_url?.match(/\.pdf$/i) ? 'application/pdf' : 'image',
          url: doc.document_url,
          document_id: doc.document_id,
          document_url: doc.document_url, // Store the URL
          originFileObj: null, // no local File object available for remote files
          isExisting: true // Mark as existing document
        }));

        // Store existing documents separately
        setExistingDocuments(mappedDocuments);
        setRemovedDocumentIds([]);

        setFormData({
          coach_type: coachData.coach_type || "",
          title: coachData.title || "",
          first_name: coachData.first_name || "",
          last_name: coachData.last_name || "",
          company_name: coachData.coach_type === 'Individual' ? '' : (coachData.company_name || ""),
          display_name: coachData.display_name || "",
          email: coachData.email || "",
          work_phone: coachData.work_phone || "",
          mobile_number: coachData?.mobile_number || "",
          isPortalEnabled: coachData.isPortalEnabled ? true : false,
          documents: mappedDocuments,
          addressLine1: addressLines[0] || "",
          addressLine2: addressLines[1] || "",
          city: coachData.city || 0,
          state: coachData.state || 0,
          pincode: coachData.pincode || "",
          phone_landline: coachData.phone_landline || "",
          country: coachData.country || 0,
          remarks: coachData.remarks || ""
        });

        // Fetch states and cities for edit mode
        if (coachData.country) {
          dispatch(getStateList(coachData.country));
        }
        if (coachData.state) {
          dispatch(getCityList(coachData.state));
        }

        // In edit mode, consider tab1 and tab2 as validated
        setTabsValidated({ tab1: true, tab2: true, tab3: true });
      } else {
        // Add mode - reset to initial state
        setFormData(initialFormState);
        setExistingDocuments([]);
        setRemovedDocumentIds([]);
        dispatch(clearStates());
        dispatch(clearCities());
        setTabsValidated({ tab1: false, tab2: true, tab3: true });
      }
      setFormErrors(initialErrorState);
      setTab("tab1");
    }
  }, [dialogOpen, coachData, dispatch]);

  const titleOptions = [
    { label: "Mr.", value: "Mr" },
    { label: "Mrs.", value: "Mrs" },
    { label: "Miss", value: "Miss" },
    { label: "Ms.", value: "Ms" },
    { label: "Mx.", value: "Mx" },
    { label: "Dr.", value: "Dr" },
    { label: "Prof.", value: "Prof" },

    { label: "Other", value: "Other" },
  ];

  // eslint-disable-next-line no-unused-vars
  const coachTypeOptions = [
    { value: "Business", label: "Business" },
    { value: "Individual", label: "Individual" }
  ];

  // Convert API data to Select options format
  const countryOptions = countryList.map(country => ({
    value: country.country_id.toString(),
    label: country.country_name
  }));

  const stateOptions = stateList.map(state => ({
    value: state.state_id.toString(),
    label: state.state_name
  }));

  const cityOptions = cityList.map(city => ({
    value: city.city_id.toString(),
    label: city.city_name
  }));

  // Handle country change - fetch states when country is selected
  const handleCountryChange = (value) => {
    handleInputChange("country", value);

    handleInputChange("state", 0);
    handleInputChange("city", 0);
    dispatch(clearCities());

    if (value) {
      dispatch(getStateList(value));
    } else {
      dispatch(clearStates());
    }
  };

  // Handle state change - fetch cities when state is selected
  const handleStateChange = (value) => {
    handleInputChange("state", value);

    // Reset city when state changes
    handleInputChange("city", 0);

    // Fetch cities for selected state
    if (value) {
      dispatch(getCityList(value));
    } else {
      dispatch(clearCities());
    }
  };

  const handleInputChange = (field, value) => {
    // For Segment components, value comes as an array
    const processedValue = Array.isArray(value) ? value[0] : value;

    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
      // If coach_type is changed to Individual, always clear company_name
      ...(field === 'coach_type' && processedValue === 'Individual' ? { company_name: '' } : {})
    }));

    // Clear error for this field
    if (formError[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  // Specific handler for Phone Inputs to validate length against country code
  const handlePhoneChange = (field, value, country) => {
    handleInputChange(field, value);

    // Validation logic:
    // If we have a value and country data, check if the length matches the format
    if (value && country && country.format) {
      // The format usually contains '.' for digits (e.g. "+1 (...) ...-....")
      // We count the number of dots to get the required number of digits (including dial code)
      const requiredLength = (country.format.match(/\./g) || []).length;
      
      // If the input length is less than the required length, show error
      if (value.length < requiredLength) {
        setFormErrors(prev => ({
          ...prev,
          [field]: "Invalid phone number"
        }));
      } else {
        // If length is sufficient, clear specific phone error
        setFormErrors(prev => ({
          ...prev,
          [field]: ""
        }));
      }
    } else {
      // If empty or no country data, clear error (unless it was empty and required, handled in validateTab)
      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleFileChange = (files) => {
    setFormData(prev => ({
      ...prev,
      documents: files
    }));
    setFormErrors(prevErr => ({
      ...prevErr,
      documents: ""
    }));
  };

  const handleFileRemove = (files) => {
    // Track which existing documents were removed
    if (isEdit) {
      const currentDocIds = files.filter(f => f.isExisting && f.document_id).map(f => f.document_id);
      const allExistingDocIds = existingDocuments.map(d => d.document_id);
      const removed = allExistingDocIds.filter(id => !currentDocIds.includes(id));
      setRemovedDocumentIds(removed);
    }

    setFormData(prev => ({
      ...prev,
      documents: files
    }));
    setFormErrors(prevErr => ({
      ...prevErr,
      documents: ""
    }));
  };

  const validateTab1 = () => {
    const errors = {};

    if (!formData.coach_type) {
      errors.coach_type = "Coach type is required";
    }
    if (!formData.first_name) {
      errors.first_name = "First name is required";
    }
    if (!formData.last_name) {
      errors.last_name = "Last name is required";
    }
    if (!formData.display_name) {
      errors.display_name = "Display name is required";
    }
    if (!formData.email) {
      errors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Preserve phone errors detected during typing
    if (formError.work_phone) errors.work_phone = formError.work_phone;
    if (formError.mobile_number) errors.mobile_number = formError.mobile_number;

    setFormErrors(prev => ({ ...prev, ...errors }));
    const isValid = Object.keys(errors).length === 0;

    // Update tab validation status
    setTabsValidated(prev => ({ ...prev, tab1: isValid }));

    return isValid;
  };

  const validateTab2 = () => {
    const errors = {};
    // REMOVED MANDATORY DOCUMENT CHECK
    // if (!formData.documents || formData.documents.length === 0) {
    //   errors.documents = "Documents are required";
    // }
    
    setFormErrors(prev => ({ ...prev, ...errors }));
    const isValid = Object.keys(errors).length === 0;

    // Update tab validation status
    setTabsValidated(prev => ({ ...prev, tab2: isValid }));

    return isValid;
  };

  const validateTab3 = () => {
    const errors = {};
    
    // Preserve phone errors detected during typing
    if (formError.phone_landline) errors.phone_landline = formError.phone_landline;

    setFormErrors(prev => ({ ...prev, ...errors }));
    const isValid = Object.keys(errors).length === 0;

    // Update tab validation status
    setTabsValidated(prev => ({ ...prev, tab3: isValid }));

    return isValid;
  };

  const handleTabClick = (targetTab) => {
    // Allow navigation to tab1 always
    if (targetTab === "tab1") {
      setTab(targetTab);
      return;
    }

    // For tab2, validate tab1 first
    if (targetTab === "tab2") {
      if (tabsValidated.tab1 || validateTab1()) {
        setTab(targetTab);
      }
      return;
    }

    // For tab3, validate tab1 and tab2 first
    if (targetTab === "tab3") {
      const tab1Valid = tabsValidated.tab1 || validateTab1();
      const tab2Valid = tabsValidated.tab2 || validateTab2();

      if (tab1Valid && tab2Valid) {
        setTab(targetTab);
      } else if (!tab1Valid) {
        setTab("tab1");
      } else if (!tab2Valid) {
        setTab("tab2");
      }
      return;
    }
  };

  const handleNext = () => {
    if (tab === "tab1" && validateTab1()) {
      setTab("tab2");
    } else if (tab === "tab2" && validateTab2()) {
      setTab("tab3");
    }
  };

  const handleClose = () => {
    // Reset form and errors when closing
    setFormData(initialFormState);
    setFormErrors(initialErrorState);
    setExistingDocuments([]);
    setRemovedDocumentIds([]);
    setTab("tab1");
    setButtonDisabled(false);
    setTabsValidated({ tab1: false, tab2: true, tab3: true });
    onDialogClose();
  };
  const handleSubmit = async () => {
    if (!validateTab1() || !validateTab2() || !validateTab3()) {
      // If validation fails, go back to the first tab with errors
      if (!validateTab1()) {
        setTab("tab1");
      } else if (!validateTab2()) {
        setTab("tab2");
      }
      return;
    }

    setButtonDisabled(true);

    // Prepare data according to API format
    const address = [formData.addressLine1, formData.addressLine2]
      .filter(line => line.trim())
      .join('\n');

    // For UPDATE case: Need to convert existing documents (URLs) to File objects
    const prepareDocumentsForSubmit = async () => {
      const documentsToSend = [];

      for (const doc of formData.documents) {
        if (doc.isExisting && doc.document_url) {
          // Existing document - fetch and convert to File object
          try {
            const response = await fetch(doc.document_url);
            const blob = await response.blob();
            const fileName = doc.name || doc.document_url.split('/').pop();
            const file = new File([blob], fileName, { type: blob.type });
            documentsToSend.push(file);
          } catch (error) {
            console.error('Error fetching existing document:', error);
            // If fetch fails, skip this document
          }
        } else {
          // New file - use as is
          const fileObj = doc?.originFileObj || doc;
          if (fileObj && fileObj instanceof File) {
            documentsToSend.push(fileObj);
          }
        }
      }

      return documentsToSend;
    };

    try {
      let documentsToSend = [];

      if (isEdit) {
        // For update: convert all documents (existing + new) to File objects
        documentsToSend = await prepareDocumentsForSubmit();
      } else {
        // For add: only get new files
        documentsToSend = formData.documents
          .map(doc => doc?.originFileObj || doc)
          .filter(fileObj => fileObj && fileObj instanceof File);
      }

      // --- CHANGE START: Always use FormData ---
      const fd = new FormData();
      fd.append('coach_type', formData.coach_type || '');
      fd.append('title', formData.title || '');
      fd.append('first_name', formData.first_name || '');
      fd.append('last_name', formData.last_name || '');
      fd.append('company_name', formData.company_name || '');
      fd.append('display_name', formData.display_name || '');
      fd.append('email', formData.email || '');
      fd.append('work_phone', formData.work_phone || '');
      fd.append('mobile_number', formData.mobile_number || '');
      fd.append('isPortalEnabled', formData.isPortalEnabled);
      fd.append('address', address || '');
      fd.append('city', formData.city ? parseInt(formData.city) : 0);
      fd.append('state', formData.state ? parseInt(formData.state) : 0);
      fd.append('pincode', formData.pincode || '');
      fd.append('phone_landline', formData.phone_landline || '');
      fd.append('country', formData.country ? parseInt(formData.country) : 0);
      fd.append('remarks', formData.remarks || '');

      // Handle Documents Logic
      if (documentsToSend.length > 0) {
        // Append all documents if they exist
        documentsToSend.forEach((file, idx) => {
          fd.append('documents', file, file.name || `file_${idx}`);
        });
      } else {
        // --- KEY FIX: Send 'documents' key as empty string if no files ---
        fd.append('documents', ""); 
      }

      // Dispatch action
      if (isEdit) {
        dispatch(updateCoach({ id: coachData.id, formData: fd }));
      } else {
        dispatch(addCoach(fd));
      }
      // --- CHANGE END ---

    } catch (error) {
      console.error('Error preparing documents:', error);
      setButtonDisabled(false);
      SweetAlertService.showError({
        title: 'Error!',
        message: 'Failed to prepare documents. Please try again.',
      });
    }
  };
  // const handleSubmit = async () => {
  //   if (!validateTab1() || !validateTab2() || !validateTab3()) {
  //     // If validation fails, go back to the first tab with errors
  //     if (!validateTab1()) {
  //       setTab("tab1");
  //     } else if (!validateTab2()) {
  //       setTab("tab2");
  //     }
  //     return;
  //   }

  //   setButtonDisabled(true);

  //   // Prepare data according to API format
  //   const address = [formData.addressLine1, formData.addressLine2]
  //     .filter(line => line.trim())
  //     .join('\n');

  //   // For UPDATE case: Need to convert existing documents (URLs) to File objects
  //   const prepareDocumentsForSubmit = async () => {
  //     const documentsToSend = [];

  //     for (const doc of formData.documents) {
  //       if (doc.isExisting && doc.document_url) {
  //         // Existing document - fetch and convert to File object
  //         try {
  //           const response = await fetch(doc.document_url);
  //           const blob = await response.blob();
  //           const fileName = doc.name || doc.document_url.split('/').pop();
  //           const file = new File([blob], fileName, { type: blob.type });
  //           documentsToSend.push(file);
  //         } catch (error) {
  //           console.error('Error fetching existing document:', error);
  //           // If fetch fails, skip this document
  //         }
  //       } else {
  //         // New file - use as is
  //         const fileObj = doc?.originFileObj || doc;
  //         if (fileObj && fileObj instanceof File) {
  //           documentsToSend.push(fileObj);
  //         }
  //       }
  //     }

  //     return documentsToSend;
  //   };

  //   try {
  //     let documentsToSend = [];

  //     if (isEdit) {
  //       // For update: convert all documents (existing + new) to File objects
  //       documentsToSend = await prepareDocumentsForSubmit();
  //     } else {
  //       // For add: only get new files
  //       documentsToSend = formData.documents
  //         .map(doc => doc?.originFileObj || doc)
  //         .filter(fileObj => fileObj && fileObj instanceof File);
  //     }

  //     if (documentsToSend.length > 0) {
  //       // Use FormData if there are documents
  //       const fd = new FormData();
  //       fd.append('coach_type', formData.coach_type || '');
  //       fd.append('title', formData.title || '');
  //       fd.append('first_name', formData.first_name || '');
  //       fd.append('last_name', formData.last_name || '');
  //       fd.append('company_name', formData.company_name || '');
  //       fd.append('display_name', formData.display_name || '');
  //       fd.append('email', formData.email || '');
  //       fd.append('work_phone', formData.work_phone || '');
  //       fd.append('mobile_number', formData.mobile_number || '');
  //       fd.append('isPortalEnabled', formData.isPortalEnabled );
  //       fd.append('address', address || '');
  //       fd.append('city', formData.city ? parseInt(formData.city) : 0);
  //       fd.append('state', formData.state ? parseInt(formData.state) : 0);
  //       fd.append('pincode', formData.pincode || '');
  //       fd.append('phone_landline', formData.phone_landline || '');
  //       fd.append('country', formData.country ? parseInt(formData.country) : 0);
  //       fd.append('remarks', formData.remarks || '');

  //       // Append all documents in binary form
  //       documentsToSend.forEach((file, idx) => {
  //         fd.append('documents', file, file.name || `file_${idx}`);
  //       });

  //       if (isEdit) {
  //         dispatch(updateCoach({ id: coachData.id, formData: fd }));
  //       } else {
  //         dispatch(addCoach(fd));
  //       }
  //     } else {
  //       // No documents - send JSON payload
  //       const submitData = {
  //         coach_type: formData.coach_type,
  //         title: formData.title,
  //         first_name: formData.first_name,
  //         last_name: formData.last_name,
  //         company_name: formData.company_name,
  //         display_name: formData.display_name,
  //         email: formData.email,
  //         work_phone: formData.work_phone,
  //         mobile_number: formData.mobile_number,
  //         isPortalEnabled: formData.isPortalEnabled,
  //         address: address,
  //         city: parseInt(formData.city) || 0,
  //         state: parseInt(formData.state) || 0,
  //         pincode: formData.pincode,
  //         phone_landline: formData.phone_landline,
  //         country: parseInt(formData.country) || 0,
  //         remarks: formData.remarks,
  //         documents: []
  //       };

  //       if (isEdit) {
  //         dispatch(updateCoach({ id: coachData.id, formData: submitData }));
  //       } else {
  //         dispatch(addCoach(submitData));
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error preparing documents:', error);
  //     setButtonDisabled(false);
  //     SweetAlertService.showError({
  //       title: 'Error!',
  //       message: 'Failed to prepare documents. Please try again.',
  //     });
  //   }
  // };

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={handleClose}
      width={900}
      onRequestClose={handleClose}
      bodyOpenClassName="overflow-hidden"
    >
      <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg width="18" height="23" viewBox="0 0 18 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.6603 20.6329C17.1934 19.3064 15.6575 18.2858 14.2952 17.6494C13.9129 17.2932 13.4338 17.0365 12.9248 16.836C12.6256 16.7183 12.4743 16.3807 12.5913 16.0811C12.7925 15.5669 13.0828 15.103 13.4718 14.6827C13.9226 14.1957 14.252 13.4743 14.4242 13.0454C14.5047 12.845 14.679 12.693 14.8922 12.659C15.7298 12.5253 16.3267 11.938 16.5264 11.0699C16.7742 9.99301 16.4929 8.82599 15.4615 8.37812C15.2991 8.30769 15.1947 8.14672 15.2111 7.97011L15.2152 7.92614C15.2301 7.76182 15.1757 7.60681 15.0803 7.48124C15.2204 7.27519 15.1441 6.89848 15.1332 6.66187C15.1213 6.39806 15.1016 6.13425 15.0718 5.87193C15.0207 5.42107 14.9414 4.97282 14.824 4.53426C14.6164 3.75848 14.2874 3.00916 13.7948 2.37125C12.794 1.07569 11.2398 0.477272 9.6432 0.364743C9.3548 -0.123377 8.60361 -0.121141 8.31782 0.368842C5.92939 0.574523 3.97169 1.9774 3.22982 4.29952C3.07817 4.77385 2.97607 5.26346 2.91086 5.7568C2.87658 6.01762 2.85236 6.27994 2.83709 6.54263C2.82181 6.80383 2.72419 7.27705 2.89745 7.49987C2.81138 7.62209 2.76331 7.77001 2.77747 7.92577L2.78194 7.97309C2.79796 8.14896 2.69475 8.30918 2.53341 8.38035C1.51245 8.83084 1.23337 10.0027 1.48935 11.0654C1.69243 11.9093 2.29047 12.491 3.10835 12.6434C3.30993 12.681 3.47351 12.8256 3.55548 13.0138C3.79395 13.5608 4.28729 14.3921 4.61817 14.771C4.95948 15.1615 5.23447 15.5893 5.41779 16.0632C5.53293 16.3609 5.37792 16.6974 5.07983 16.8118C4.51607 17.0283 3.98585 17.3107 3.57784 17.7172C2.45069 18.2612 1.25312 19.0031 0.600675 20.1079C0.143109 20.8826 -0.0908908 21.99 0.0328161 22.8906L0.770586 22.7896C0.714694 22.1737 0.830949 21.5283 1.0329 20.9481C1.34105 20.0621 2.11459 19.4372 2.88404 18.9521C2.53676 20.3963 3.18101 21.9479 4.66623 22.3801C6.17605 22.8194 7.6039 22.0246 8.29435 20.7089C8.36812 20.5681 8.51829 20.4828 8.67702 20.4958C8.87674 20.5126 9.07646 20.5141 9.27655 20.501C9.43193 20.491 9.57799 20.5755 9.65065 20.7134C10.3493 22.0429 11.8081 22.8347 13.3253 22.3656C14.8009 21.9095 15.4183 20.3304 15.0434 18.8984C15.8185 19.375 16.6304 19.998 16.9579 20.8796C17.1755 21.465 17.313 22.1606 17.2433 22.7892L17.9811 22.8902C17.986 22.8514 18.1085 21.9072 17.6603 20.6329C18.1085 21.9072 17.1934 19.3064 17.6603 20.6329ZM6.41788 17.4303C6.41788 17.1885 6.39776 16.9489 6.35975 16.7131C6.31653 16.447 6.574 16.2369 6.82291 16.3401C7.27861 16.529 7.76114 16.6613 8.2623 16.7276C8.40315 16.7462 8.50636 16.8695 8.49034 17.0108C8.40166 17.7836 8.25485 18.5728 8.04619 19.2803C7.99551 19.4521 7.8066 19.5456 7.64302 19.4726C7.23315 19.2897 6.86203 19.0273 6.55276 18.7032C6.45253 18.5985 6.39738 18.4591 6.40186 18.3142C6.41229 17.9986 6.41788 17.4303 6.41788 17.4303ZM8.5671 14.2434C8.63752 14.2724 8.71428 14.2888 8.79476 14.2888H9.14688C9.22736 14.2888 9.30412 14.2724 9.37455 14.2434V14.4692C9.37455 14.9886 8.5671 14.992 8.5671 14.4692V14.2434ZM11.5946 17.4303C11.5946 17.4303 11.6035 18.0053 11.6184 18.3268C11.6251 18.4762 11.5677 18.6204 11.4634 18.7278C11.1351 19.0642 10.739 19.3314 10.3027 19.5102C10.1399 19.5769 9.95694 19.4842 9.90626 19.3154C9.69238 18.6003 9.54259 17.7996 9.45242 17.0152C9.4364 16.8751 9.54036 16.7525 9.68046 16.7365C10.2051 16.6754 10.71 16.5428 11.1858 16.346C11.4362 16.2428 11.6952 16.4511 11.6519 16.7186C11.6147 16.953 11.5949 17.1907 11.5946 17.4303ZM8.98144 4.50892C10.8356 4.50892 12.1188 5.1595 12.9449 5.81791C13.0399 5.89355 12.9576 6.04222 12.8428 6.00309C10.3221 5.1405 7.68028 5.13528 5.15622 5.9908C5.04183 6.02955 4.96022 5.88125 5.05449 5.80561C5.87051 5.15168 7.13888 4.50892 8.98144 4.50892ZM2.1526 10.4931C2.07621 9.54141 2.59265 8.94076 3.5484 8.92064C3.58939 8.92921 3.61994 8.92883 3.66205 8.92436C4.0354 8.96572 4.38156 8.68403 4.40354 8.28571L4.44416 7.5539C4.4613 7.24463 4.65878 6.97486 4.94793 6.86308C6.23232 6.36676 7.6039 6.06085 8.98144 6.06085C10.3702 6.06085 11.7533 6.37161 13.047 6.875C13.335 6.98716 13.5318 7.25656 13.5489 7.56508L13.5891 8.28534C13.6123 8.70229 13.9908 8.99143 14.3836 8.91505C15.3654 8.92623 15.937 9.49707 15.861 10.4931C15.8084 11.1805 15.5237 11.84 14.7729 11.9306C14.8374 11.5751 14.3802 11.3441 14.1347 11.612C13.936 11.8285 13.9066 12.3297 13.8019 12.6069C13.566 13.2299 13.2206 13.8104 12.7795 14.3097C11.9582 15.2397 10.8139 15.8549 9.58209 15.9976C9.46584 16.0111 9.36262 15.9194 9.35815 15.8024C9.35741 15.7849 9.35703 15.767 9.35629 15.7495C9.35256 15.634 9.41665 15.5289 9.518 15.4737C9.87571 15.2796 10.1201 14.9037 10.1201 14.4688V13.5671C10.1201 13.4676 10.1701 13.3741 10.2547 13.3212C10.4316 13.2098 10.6463 13.0492 10.7025 12.8793C10.7618 12.7004 10.6351 12.5037 10.4391 12.5037H7.56105C7.36468 12.5037 7.238 12.7004 7.29761 12.8793C7.34978 13.0365 7.53459 13.1814 7.6937 13.2869C7.77493 13.3405 7.82299 13.4318 7.82299 13.5295V14.4692C7.82299 14.904 8.06743 15.28 8.42513 15.4741C8.52648 15.5293 8.59057 15.6343 8.58685 15.7499C8.58647 15.7651 8.58573 15.7808 8.58536 15.7961C8.58126 15.9138 8.47618 16.0058 8.35918 15.9909C7.15192 15.8382 6.08216 15.2684 5.28514 14.4058C4.77317 13.8126 4.37224 13.1714 4.12818 12.4239C4.0313 12.1273 4.02646 11.4861 3.72912 11.3236C3.58045 11.2424 3.39787 11.2748 3.2831 11.3985C3.13778 11.5557 3.19293 11.7294 3.23094 11.9175C2.5744 11.8054 2.21967 11.3374 2.1526 10.4931C2.21967 11.3374 2.12354 10.1342 2.1526 10.4931ZM4.57718 21.5544C3.46009 21.0286 3.20634 19.3854 3.93256 18.4367C3.98622 18.3667 4.06 18.3138 4.14011 18.2769C4.313 18.1979 4.48142 18.1256 4.64164 18.0608C4.90992 17.9524 5.22105 18.0604 5.35817 18.3156C5.7524 19.0489 6.36012 19.6433 7.0856 20.0282C7.43436 20.213 7.51894 20.6828 7.23278 20.9549C6.57847 21.576 5.40512 21.9438 4.57718 21.5544ZM8.73999 19.5661C8.79588 19.3847 8.84767 19.1995 8.895 19.0113C8.91512 18.9308 9.0269 18.9308 9.04702 19.0113C9.09472 19.2002 9.14688 19.3862 9.20277 19.5684C9.23221 19.6645 9.16216 19.7606 9.06155 19.7599C9.00194 19.7595 8.93747 19.7588 8.87748 19.7573C8.77874 19.7554 8.71093 19.6604 8.73999 19.5661ZM14.3877 20.0259C14.3027 21.0286 13.5098 21.7232 12.5149 21.7496C11.7913 21.7686 11.1608 21.4724 10.71 20.9709C10.4518 20.6832 10.5434 20.2357 10.8881 20.0606C11.637 19.6802 12.266 19.0784 12.6722 18.3302C12.8123 18.0723 13.123 17.9598 13.3946 18.0708C13.5109 18.1185 13.6313 18.17 13.7542 18.2247C13.8284 18.2579 13.8977 18.3052 13.9506 18.3671C14.3176 18.7959 14.4469 19.328 14.3877 20.0259C14.4514 19.2777 14.3128 20.909 14.3877 20.0259Z" fill="#344054" />
            </svg>

          </span>
          <div>
            <h5 className="mb-0 text-gray-900 text-base font-semibold">
              {isEdit ? "Edit Coach" : "Add Coach"}
            </h5>
            <p className="text-sm text-gray-600 mb-0">
              {isEdit ? "Update coach details" : "Create a new Coach"}
            </p>
          </div>
        </div>

        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <Tabs value={tab} variant="pill">
        {/* Custom styled tab navigation */}
        <div className="px-6 mb-5">
          <div className="p-1 rounded-lg client-portal-tab" style={{ backgroundColor: '#FFF4E5' }}>
            <TabList>
              <TabNav
                value="tab1"
                onClick={() => handleTabClick("tab1")}
                style={{
                  backgroundColor: tab === 'tab1' ? '#ffffff' : 'transparent',
                  color: '#344054',
                  fontWeight: tab === 'tab1' ? '600' : '500'
                }}
              >
                Basic Details
              </TabNav>
              <TabNav
                value="tab2"
                onClick={() => handleTabClick("tab2")}
                style={{
                  backgroundColor: tab === 'tab2' ? '#ffffff' : 'transparent',
                  color: '#344054',
                  fontWeight: tab === 'tab2' ? '600' : '500',
                  cursor: tabsValidated.tab1 ? 'pointer' : 'not-allowed',
                  opacity: tabsValidated.tab1 ? 1 : 0.6
                }}
              >
                Other Details
              </TabNav>
              <TabNav
                value="tab3"
                onClick={() => handleTabClick("tab3")}
                style={{
                  backgroundColor: tab === 'tab3' ? '#ffffff' : 'transparent',
                  color: '#344054',
                  fontWeight: tab === 'tab3' ? '600' : '500',
                  cursor: (tabsValidated.tab1 && tabsValidated.tab2) ? 'pointer' : 'not-allowed',
                  opacity: (tabsValidated.tab1 && tabsValidated.tab2) ? 1 : 0.6
                }}
              >
                Address Details
              </TabNav>
            </TabList>
          </div>
        </div>

        <TabContent value="tab1">
          <div className="px-6">
            {/* Coach Type */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Coach type*
              </label>
              <div className="w-3/4 pl-2">
                <Segment
                  className="docType-btn"
                  selectionType="single"
                  value={formData.coach_type ? [formData.coach_type] : []}
                  onChange={(val) => handleInputChange("coach_type", val)}
                >
                  <Segment.Item value="Business">Business</Segment.Item>
                  <Segment.Item value="Individual">Individual</Segment.Item>
                </Segment>
                {formError.coach_type && (
                  <p className="text-xs text-red-600 mb-2 mt-1">
                    {formError.coach_type}
                  </p>
                )}
              </div>
            </div>

            {/* Name Info (Title, First Name, Last Name) */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Name Info*
              </label>
              <div className="w-3/4 pl-2 flex gap-3">
                <div style={{ width: '120px' }}>
                  <Select
                    className="h-9 select-options"
                    placeholder="Title"
                    options={titleOptions}
                    value={titleOptions.find(opt => opt.value === formData.title) || null}
                    onChange={(selectedOption) => handleInputChange("title", selectedOption?.value || "")}
                  />

                </div>
                <div className="flex-1">
                  <input
                    className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={formData.first_name}
                    placeholder="Enter First name here"
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                  />
                  {formError.first_name && (
                    <p className="text-xs text-red-600 mb-2 mt-1">
                      {formError.first_name}
                    </p>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={formData.last_name}
                    placeholder="Enter Last name here"
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                  />
                  {formError.last_name && (
                    <p className="text-xs text-red-600 mb-2 mt-1">
                      {formError.last_name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Company Name - Only show when Coach Type is Business */}
            {formData.coach_type === "Business" && (
              <div className="flex mb-5">
                <label className="font-medium text-gray-700 w-1/4">
                  Company Name
                </label>
                <div className="w-3/4 pl-2">
                  <input
                    className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={formData.company_name}
                    placeholder="Enter Company name here"
                    onChange={(e) => handleInputChange("company_name", e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Display Name */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Display Name*
              </label>
              <div className="w-3/4 pl-2">
                <input
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={formData.display_name}
                  placeholder="Display Name here"
                  onChange={(e) => handleInputChange("display_name", e.target.value)}
                />
                {formError.display_name && (
                  <p className="text-xs text-red-600 mb-2 mt-1">
                    {formError.display_name}
                  </p>
                )}
              </div>
            </div>

            {/* Email Address */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Email Address*
              </label>
              <div className="w-3/4 pl-2">
                <input
                disabled={isEdit && true}
                  type="email"
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={formData.email}
                  placeholder="Enter Email address here"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
                {formError.email && (
                  <p className="text-xs text-red-600 mb-2 mt-1">
                    {formError.email}
                  </p>
                )}
              </div>
            </div>

            {/* Phone (Work and Mobile) */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Phone
              </label>
              <div className="w-3/4 pl-2 flex gap-3">
                <div className="flex-1">
                  <CommonPhoneInput
                    value={formData.work_phone}
                    onChange={(phone, country) => handlePhoneChange("work_phone", phone, country)}
                    placeholder="Enter Work phone number here"
                    error={formError.work_phone}
                  />
                </div>
                <div className="flex-1">
                  <CommonPhoneInput
                    value={formData.mobile_number}
                    onChange={(phone, country) => handlePhoneChange("mobile_number", phone, country)}
                    placeholder="Enter Mobile number here"
                    error={formError.mobile_number}
                  />
                </div>
              </div>
            </div>
          </div>
        </TabContent>

        <TabContent value="tab2">
          <div className="px-6">
            {/* Enable Portal */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Enable Portal*
              </label>
              <div className="w-3/4 pl-2">
                <Segment
                  className="docType-btn"
                  selectionType="single"
                  value={formData.isPortalEnabled ? ["yes"] : ["no"]}
                  onChange={(val) => handleInputChange("isPortalEnabled", val[0] === "yes")}
                >
                  <Segment.Item value="yes">Yes</Segment.Item>
                  <Segment.Item value="no">No</Segment.Item>
                </Segment>
                {formError.isPortalEnabled && (
                  <p className="text-xs text-red-600 mb-2 mt-1">
                    {formError.isPortalEnabled}
                  </p>
                )}
              </div>
            </div>

            {/* Documents Upload Section */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Documents
              </label>
              <div className="w-3/4 pl-2">
                <Upload
                  draggable
                  showList={true}
                  className="border-2 border-dashed border-gray-300 rounded-lg py-8 px-6 w-full text-center hover:border-gray-400 transition-colors"
                  onChange={handleFileChange}
                  onFileRemove={handleFileRemove}
                  multiple={true}
                  maxFileSize={25 * 1024 * 1024}
                  fileList={formData.documents}
                >
                  <div className="flex justify-center mb-3">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="40" height="40" rx="20" fill="#F2F4F7" />
                      <path d="M20 16v8m-4-4h8" stroke="#475467" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-orange-500 mb-1">
                    Click to upload file
                  </p>

                </Upload>
                {formError.documents && (
                  <p className="text-xs text-red-600 mb-2 mt-1">
                    {formError.documents}
                  </p>
                )}
              </div>
            </div>
          </div>
        </TabContent>

        <TabContent value="tab3">
          <div className="px-6">
             {/* Country/State/City */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Country/State/City
              </label>
              <div className="w-3/4 pl-2 flex gap-3">
                <div className="flex-1">
                  <Select
                    className="w-full h-9 select-options"
                    placeholder="Select Country"
                    options={countryOptions}
                    value={countryOptions.find(c => c.value === formData.country.toString()) || null}
                    onChange={(selectedOption) => handleCountryChange(selectedOption?.value || 0)}
                  />
                </div>
                <div className="flex-1">
                  <Select
                    className={`w-full h-9 select-options ${(!formData.country || stateOptions.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder="State"
                    options={stateOptions}
                    value={stateOptions.find(s => s.value === formData.state.toString()) || null}
                    onChange={(selectedOption) => handleStateChange(selectedOption?.value || 0)}
                    isDisabled={!formData.country || stateOptions.length === 0}
                  />
                </div>
                <div className="flex-1">
                  <Select
                    className={`w-full h-9 select-options ${(!formData.state || cityOptions.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder="City"
                    options={cityOptions}
                    value={cityOptions.find(c => c.value === formData.city.toString()) || null}
                    onChange={(selectedOption) => handleInputChange("city", selectedOption?.value || 0)}
                    isDisabled={!formData.state || cityOptions.length === 0}
                  />
                </div>
              </div>
            </div>
            {/* Address Line 1 */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Address
              </label>
              <div className="w-3/4 pl-2">
                <input
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-3"
                  value={formData.addressLine1}
                  placeholder="Line 1"
                  onChange={(e) => handleInputChange("addressLine1", e.target.value)}
                />
                <input
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={formData.addressLine2}
                  placeholder="Line 2"
                  onChange={(e) => handleInputChange("addressLine2", e.target.value)}
                />
              </div>
            </div>
          

            {/* Pincode/Landline */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Pincode/Landline
              </label>
              <div className="w-3/4 pl-2 flex gap-3">
                <div className="flex-1">
                  <input
                    type="tel"
                    className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={formData.pincode}
                    placeholder="Pincode"
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange("pincode", value);
                    }}
                  />
                </div>
                <div className="flex-1">
                  <CommonPhoneInput
                    value={formData.phone_landline}
                    onChange={(phone, country) => handlePhoneChange("phone_landline", phone, country)}
                    placeholder="Phone Landline"
                    error={formError.phone_landline}
                  />
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 w-1/4">
                Remarks
              </label>
              <div className="w-3/4 pl-2">
                <textarea rows={4}
                  className="pl-3 pt-2 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={formData.remarks}
                  placeholder="Enter remarks here"
                  onChange={(e) => handleInputChange("remarks", e.target.value)}
                />
              </div>
            </div>
          </div>
        </TabContent>
      </Tabs>

      <div className="flex dailog-footer">
        <button
          className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 duration-100 secondary-btn"
          variant="plain"
          onClick={handleClose}
          style={{ backgroundColor: '#FFF4E5' }}
        >
          Cancel
        </button>

        {(tab === "tab1" || tab === "tab2") && (
          <BtnPrimary
            onClick={handleNext}
            className="ml-1.5"
            style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
          >
            Next
          </BtnPrimary>
        )}

        {tab === "tab3" && (
          <BtnPrimary
            disabled={buttonDisabled}
            onClick={handleSubmit}
            className="ml-1.5"
            style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
          >
            {buttonDisabled ? <ThreeDotLoader /> : isEdit ? "Update Coach" : "Add Coach"}
          </BtnPrimary>
        )}
      </div>
    </Dialog>
  );
};

export default AddEditCoach;